import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  Alert,
  AlertDescription,
  Button,
  DatePicker,
  EmptyState,
  Input,
  ReportTable,
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
  type ReportTableColumn,
  type ReportTablePaginationState,
  type ReportTableSort,
} from '@roger/ui-components';
import { ArrowLeft, Download } from 'lucide-react';
import { PermissionSlug } from '../constants/featureFlags';
import {
  CURRENT_MODE,
  DATE_RANGE_MODE,
  SORT_FIELDS,
  useRehospitalizationReportParams,
} from '../hooks/reportParams/useRehospitalizationReportParams';
import {
  exportRehospitalizationReportReportsRehospitalizationExportGet,
  getRehospitalizationClinicOptionsReportsRehospitalizationClinicOptionsGet,
  getRehospitalizationReportReportsRehospitalizationGet,
} from '../services/api/sdk.gen';
import type {
  RehospitalizationReportResult,
  RehospitalizationReportRow,
  RehospitalizationSortBy,
} from '../services/api/types.gen';
import { downloadResponseAsFile } from '../utils/downloadFile';
import {
  getBusinessErrorMessage,
  getUserFacingApiMessage,
  getUserFacingApiMessageAsync,
} from '../utils/apiErrorDetail';
import {
  formatDateToISO,
  formatDateToMMDDYYYY,
  parseISOToLocalDate,
} from '../utils/dateUtils';
import { REPORT_PAGE_SIZE_OPTIONS } from '../utils/reportDisplay';
import { useReportsBackHref } from '../reporting/backHref';
import { useStore } from '../store';
import ReportsPermissionAlert from './ReportsPermissionAlert';

const ALL_CLINICS = '__all_clinics__';

const toLoadErrorMessage = (error: unknown, fallback: string): string =>
  getBusinessErrorMessage(error) ?? getUserFacingApiMessage(error, fallback);

const headers: Record<RehospitalizationSortBy, string> = {
  clinic: 'Clinic',
  patient: 'Patient',
  transfer_date: 'Transfer Date',
  reason: 'Reason for Rehospitalization',
  destination: 'Destination',
  episode_status: 'Episode Status',
};

const displayValue = (value: unknown): string =>
  value === null || value === undefined || value === ''
    ? 'Not recorded'
    : String(value);

const displayDate = (value: string | null | undefined): string => {
  if (!value || !parseISOToLocalDate(value)) return 'Not recorded';
  return formatDateToMMDDYYYY(value) || 'Not recorded';
};

const columns: ReportTableColumn<RehospitalizationReportRow>[] =
  SORT_FIELDS.map(sortBy => ({
    id: sortBy,
    header: headers[sortBy],
    accessor: (row: RehospitalizationReportRow) => {
      if (sortBy === 'transfer_date') return displayDate(row.transfer_date);
      return displayValue(row[sortBy]);
    },
    enableSorting: true,
    minWidth: sortBy === 'reason' ? 240 : 150,
  }));

const RehospitalizationReport = () => {
  const hasPermission = useStore(state => state.hasPermission);
  const reportsBackHref = useReportsBackHref();
  const {
    params,
    tableQuery,
    exportQuery,
    diagnosisInput,
    setDiagnosisInput,
    isDiagnosisPending,
    dateValidationError,
    setMode,
    setDateRange,
    setClinicId,
    setSorting,
    setPagination,
    reset,
  } = useRehospitalizationReportParams();

  const [clinics, setClinics] = useState<Array<{ id: string; name: string }>>(
    []
  );
  const [report, setReport] = useState<RehospitalizationReportResult | null>(
    null
  );
  const [listError, setListError] = useState<string | null>(null);
  const [optionsError, setOptionsError] = useState<string | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [isExporting, setIsExporting] = useState(false);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    let cancelled = false;
    setOptionsError(null);
    getRehospitalizationClinicOptionsReportsRehospitalizationClinicOptionsGet()
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error(
            'Failed to load rehospitalization clinic options',
            response
          );
          setClinics([]);
          setOptionsError(
            toLoadErrorMessage(
              response,
              'Unable to load rehospitalization clinics. Please try again.'
            )
          );
          return;
        }
        setClinics(response.data.clinics ?? []);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load rehospitalization clinic options', error);
        setClinics([]);
        setOptionsError(
          toLoadErrorMessage(
            error,
            'Unable to load rehospitalization clinics. Please try again.'
          )
        );
      });
    return () => {
      cancelled = true;
    };
  }, [hasPermission]);

  useEffect(() => {
    if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) return;
    if (dateValidationError) {
      setReport(null);
      setListError(dateValidationError);
      setIsLoading(false);
      return;
    }

    let cancelled = false;
    setIsLoading(true);
    setListError(null);
    getRehospitalizationReportReportsRehospitalizationGet({ query: tableQuery })
      .then(response => {
        if (cancelled) return;
        if (response.error || !response.data) {
          console.error('Failed to load rehospitalization report', response);
          setReport(null);
          setListError(
            toLoadErrorMessage(
              response,
              'Unable to load rehospitalization report. Please try again.'
            )
          );
          return;
        }
        setReport(response.data);
      })
      .catch(error => {
        if (cancelled) return;
        console.error('Failed to load rehospitalization report', error);
        setReport(null);
        setListError(
          toLoadErrorMessage(
            error,
            'Unable to load rehospitalization report. Please try again.'
          )
        );
      })
      .finally(() => {
        if (!cancelled) setIsLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [dateValidationError, hasPermission, tableQuery]);

  const handleExport = async () => {
    if (dateValidationError || isDiagnosisPending) return;
    setIsExporting(true);
    setExportError(null);
    try {
      const response =
        await exportRehospitalizationReportReportsRehospitalizationExportGet({
          query: exportQuery,
          responseType: 'blob',
        });
      if (response.error || !response.data) {
        console.error('Failed to export rehospitalization report', response);
        setExportError(
          await getUserFacingApiMessageAsync(
            response,
            'Unable to export report. Please try again.'
          )
        );
        return;
      }
      downloadResponseAsFile(
        response as never,
        'RogerRehospitalizationReport.xlsx'
      );
    } catch (error) {
      console.error('Failed to export rehospitalization report', error);
      setExportError(
        await getUserFacingApiMessageAsync(
          error,
          'Unable to export report. Please try again.'
        )
      );
    } finally {
      setIsExporting(false);
    }
  };

  const sorting: ReportTableSort | null = params.sortBy
    ? { id: params.sortBy, direction: params.sortOrder ?? 'asc' }
    : null;
  const total = report?.total ?? 0;
  const emptyState = listError ? (
    <EmptyState
      title='Unable to load rehospitalizations'
      description='Please try again.'
    />
  ) : (
    <EmptyState
      title='No rehospitalizations found'
      description='Adjust your filters and try again.'
    />
  );

  const displayedListError =
    listError && listError !== dateValidationError ? listError : null;
  const displayedExportError =
    exportError &&
    exportError !== displayedListError &&
    exportError !== optionsError
      ? exportError
      : null;

  if (!hasPermission(PermissionSlug.ROGER_EMR_REPORTS_ACCESS)) {
    return <ReportsPermissionAlert />;
  }

  return (
    <div className='space-y-6'>
      <Link
        to={reportsBackHref}
        className='inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground'
      >
        <ArrowLeft className='h-4 w-4' aria-hidden='true' /> Reports
      </Link>
      <div className='flex flex-col gap-3 sm:flex-row sm:items-start sm:justify-end'>
        <Button
          variant='outline'
          size='sm'
          onClick={() => void handleExport()}
          disabled={
            isLoading ||
            isExporting ||
            isDiagnosisPending ||
            Boolean(dateValidationError)
          }
        >
          <Download className='h-4 w-4' aria-hidden='true' />
          {isExporting ? 'Exporting…' : 'Export'}
        </Button>
      </div>

      <div className='space-y-4 rounded-lg border bg-card p-4'>
        <div className='flex flex-wrap gap-2' aria-label='Report mode'>
          <Button
            type='button'
            size='sm'
            variant={params.mode === CURRENT_MODE ? 'default' : 'outline'}
            onClick={() => setMode('current')}
          >
            Current active episodes
          </Button>
          <Button
            type='button'
            size='sm'
            variant={params.mode === DATE_RANGE_MODE ? 'default' : 'outline'}
            onClick={() => setMode('date_range')}
          >
            Transfers in date range
          </Button>
        </div>
        <div className='flex flex-wrap items-end gap-3'>
          {params.mode === DATE_RANGE_MODE ? (
            <>
              <label className='flex min-w-[12rem] flex-col gap-1 text-sm font-medium'>
                From
                <DatePicker
                  id='rehospitalization-from'
                  aria-label='From'
                  date={parseISOToLocalDate(params.dateFrom)}
                  datePickerSize='default'
                  onDateChange={date =>
                    setDateRange(
                      date ? formatDateToISO(date) : null,
                      params.dateTo
                    )
                  }
                  showClearButton
                  error={Boolean(dateValidationError)}
                />
              </label>
              <label className='flex min-w-[12rem] flex-col gap-1 text-sm font-medium'>
                To
                <DatePicker
                  id='rehospitalization-to'
                  aria-label='To'
                  date={parseISOToLocalDate(params.dateTo)}
                  datePickerSize='default'
                  onDateChange={date =>
                    setDateRange(
                      params.dateFrom,
                      date ? formatDateToISO(date) : null
                    )
                  }
                  showClearButton
                  error={Boolean(dateValidationError)}
                />
              </label>
            </>
          ) : null}
          <label className='flex min-w-[12rem] flex-col gap-1 text-sm font-medium'>
            Clinic
            <Select
              value={params.clinicId || ALL_CLINICS}
              onValueChange={(value: string) =>
                setClinicId(value === ALL_CLINICS ? null : value)
              }
            >
              <SelectTrigger selectSize='default' aria-label='Clinic'>
                <SelectValue placeholder='All clinics' />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ALL_CLINICS}>All clinics</SelectItem>
                {clinics.map(clinic => (
                  <SelectItem key={clinic.id} value={clinic.id}>
                    {clinic.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </label>
          <label className='flex min-w-[16rem] flex-1 flex-col gap-1 text-sm font-medium'>
            Diagnosis codes
            <Input
              value={diagnosisInput}
              onChange={(event: React.ChangeEvent<HTMLInputElement>) =>
                setDiagnosisInput(event.target.value)
              }
              inputSize='default'
              aria-label='Diagnosis codes'
              placeholder='Enter diagnosis codes'
            />
          </label>
          <Button type='button' variant='outline' size='sm' onClick={reset}>
            Reset
          </Button>
        </div>
        {dateValidationError ? (
          <p role='alert' className='text-sm text-red-700'>
            {dateValidationError}
          </p>
        ) : null}
        {optionsError ? (
          <Alert className='border-red-200 bg-red-50'>
            <AlertDescription className='text-red-800'>
              {optionsError}
            </AlertDescription>
          </Alert>
        ) : null}
      </div>

      {displayedListError ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>
            {displayedListError}
          </AlertDescription>
        </Alert>
      ) : null}

      {displayedExportError ? (
        <Alert className='border-red-200 bg-red-50'>
          <AlertDescription className='text-red-800'>
            {displayedExportError}
          </AlertDescription>
        </Alert>
      ) : null}

      <ReportTable
        data={report?.items ?? []}
        columns={columns}
        getRowId={row => row.visit_id}
        caption='Rehospitalization report results'
        sorting={sorting}
        onSortingChange={next =>
          setSorting(
            next?.id as RehospitalizationSortBy | undefined,
            next?.direction
          )
        }
        isLoading={isLoading}
        className='min-w-[980px]'
        emptyState={emptyState}
        pagination={{
          page: params.page,
          pageSize: params.pageSize,
          total,
          totalPages: Math.ceil(total / params.pageSize),
          pageSizeOptions: REPORT_PAGE_SIZE_OPTIONS,
        }}
        onPaginationChange={(next: ReportTablePaginationState) =>
          setPagination(next.page, next.pageSize)
        }
      />
    </div>
  );
};

export default RehospitalizationReport;
